import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ApiComponent from "./ApiComponent";
import ResponseComponent from "../Collections/ResponseComponent";
import OverviewComponent from '../Collections/OverviewComponent.jsx';
import CollectedSamplesTable from '../Samples/CollectedSamplesTable';
import ApiOptions from './ApiOptions';
import { AppProvider } from '../../AppContext';
import { useDispatch, connect } from "react-redux";
import { getEntity } from "../../redux-store/currentUserActions";
import { getVariableSet } from 'redux-store/variableSetReducers/variableSetActions';
import { getAllVariables } from 'redux-store/variableReducers/variableActions';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { setIsRefetchEnvironment, setIsRefetchCollection, setIsRefetchFolder, setCollectionVariableDetails, setFolderVariableDetails } from 'redux-store/variableSetReducers/variableSetSlice';
import ApiTestScenarioGenerator from 'components/ApiTestScenarioGenerator/ApiTestScenarioGenerator';


const ApiScreen = ({ state, api, onEntitySlugUpdate, selectedSidebarState, entities, isRefetchEnvironment, isRefetchCollection, isRefetchFolder }) => {
  const dispatch = useDispatch();
  const [apiOverview, setApiOverview] = useState(false);
  const [sendClicked, setSendClicked] = useState(false);
  const [selectedTab, setSelectedTab] = useState('option2');
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');


  const entity = useMemo(() => {
    return entities?.[selectedSidebarState?.id] || null;
  }, [selectedSidebarState?.id, entities]);

  // Use entity as fallback for api when api is null or stale
  const effectiveApi = useMemo(() => {
    // If api exists and has an id, use it
    if (api && api.id) {
      return api;
    }
    // Otherwise, fall back to entity if it exists
    if (entity && entity.id) {
      return entity;
    }
    // If both are null, return null
    return null;
  }, [api, entity]);

  // Handle case where entity becomes null (e.g., after data refresh)
  useEffect(() => {
    if (selectedSidebarState?.id && !entity && selectedSidebarState?.type === 'entity') {
      // Try to refetch the entity if it's missing
      const fetchEntity = async () => {
        try {
          await dispatch(getEntity({ id: selectedSidebarState?.id }));
        } catch (error) {
          console.error('Error refetching entity:', error);
        }
      };
      fetchEntity();
    }
  }, [selectedSidebarState?.id, entity, dispatch, selectedSidebarState?.type]);



  const titleParts = [];
  if (state?.collection?.name) titleParts.push(state.collection.name);
  if (state?.folder) titleParts.push(state.folder.name);
  if (state?.collectionApi) titleParts.push(state.collectionApi.name);
  if (state?.folderApi) titleParts.push(state.folderApi.name);

  const fetchSelectedEnvironment = useCallback(async () => {
    if (selectedEnvironmentId) {
      await dispatch(getVariableSet({
        variableSetId: selectedEnvironmentId
      })).unwrap();
    }
  }, [selectedEnvironmentId, dispatch]);

  const fetchCollectionVariables = useCallback(async () => {
    if (!entity?.request) return null;
    if (!entity?.request?.collection_id) return null;
    const res = await dispatch(getAllVariables({ resourceId: entity.request.collection_id })).unwrap();
    const variablesJSON = res.reduce((acc, { var_name, var_value }) => {
      acc[var_name] = var_value;
      return acc;
    }, {});
    await dispatch(setCollectionVariableDetails({ selectedCollectionVariablesJSON: variablesJSON }));
  }, [entity?.request, dispatch]);

  const fetchFolderVariables = useCallback(async () => {
    if (!entity?.request) return null;
    if (!entity?.request?.folder_id) return null;
    const res = await dispatch(getAllVariables({ resourceId: entity.request.folder_id })).unwrap();
    const variablesJSON = res.reduce((acc, { var_name, var_value }) => {
      acc[var_name] = var_value;
      return acc;
    }, {});
    await dispatch(setFolderVariableDetails({ selectedFolderVariablesJSON: variablesJSON }));
  }, [entity?.request, dispatch]);

  const refetchAllVariables = useCallback(async () => {
    await fetchSelectedEnvironment();
    await fetchCollectionVariables();
    await fetchFolderVariables();
  }, [fetchSelectedEnvironment, fetchCollectionVariables, fetchFolderVariables]);

  useEffect(() => {
    refetchAllVariables()
  }, [refetchAllVariables]);

  useEffect(() => {
    if (isRefetchEnvironment) {
      fetchSelectedEnvironment();
      dispatch(setIsRefetchEnvironment(false));
    }
    if (isRefetchCollection) {
      fetchCollectionVariables();
      dispatch(setIsRefetchCollection(false));
    }
    if (isRefetchFolder) {
      fetchFolderVariables();
      dispatch(setIsRefetchFolder(false));
    }
  }, [isRefetchEnvironment, isRefetchCollection, isRefetchFolder, fetchSelectedEnvironment, fetchCollectionVariables, fetchFolderVariables, dispatch]);

  const renderTabContent = useCallback(() => {
    // Show loading state if entity is temporarily unavailable but we're trying to refetch it
    if (!entity && selectedSidebarState?.id && selectedSidebarState?.type === 'entity') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography>Loading entity data...</Typography>
        </Box>
      );
    }

    if (apiOverview) {
      return <OverviewComponent resource={entity} />
    } else if (selectedTab === 'option2') {
      return (
        <ApiComponent
          entity={entity}
          onEntitySlugUpdate={onEntitySlugUpdate}
          selectedEnvironmentId={selectedEnvironmentId}
        />
      );
    } else if (selectedTab === 'option3') {
      return <CollectedSamplesTable state={state} api={effectiveApi} />
    } else if (selectedTab === 'option4') {
      // Only render ApiTestScenarioGenerator if we have valid api/entity data
      if (!effectiveApi || !effectiveApi.id) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Typography>Loading API data...</Typography>
          </Box>
        );
      }
      return <ApiTestScenarioGenerator state={state} api={effectiveApi} />
    } return null;
  }, [apiOverview, selectedTab, state, effectiveApi, entity, onEntitySlugUpdate, selectedEnvironmentId, selectedSidebarState]);

  const handleSelectedOption = (option) => {
    setSelectedTab(option);
    setApiOverview(option === 'option1');
  }

  return (
    <AppProvider value={{ sendClicked, setSendClicked }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh', overflow: "hidden", backgroundColor: 'var(--background-primary)' }}>
        <ApiOptions selectedIcon={handleSelectedOption} entity={entity} api={effectiveApi} state={state} onSelectEnvironmentId={setSelectedEnvironmentId} sendClicked={sendClicked} selectedTab={selectedTab} />
        {selectedTab === 'option2' ? (
          <Splitter layout="vertical" style={{ height: 'calc(100vh - 123px)', maxHeight: 'calc(100vh - 123px)', border: "1px solid var(--borderColor)" }}>
            {/* Upper Splitter Panel */}
            <SplitterPanel className="overflow-y-auto bgPrimary pt-2" size={75}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                  {renderTabContent()}
                </Box>
              </Box>
            </SplitterPanel>

            {/* Lower Splitter Panel - Only for option2 */}
            <SplitterPanel className="h-full overflow-y-auto bg-[var(--background-primary)]" size={25}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                  <ResponseComponent state={state} entity={entity} />
                </Box>
              </Box>
            </SplitterPanel>
          </Splitter>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
            {renderTabContent()}
          </Box>
        )}
      </Box>
    </AppProvider >
  );
}

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    selectedSidebarState: state.user.selectedSidebarState,
    entities: state.user.entities,
    isRefetchEnvironment: state.variableSet.isRefetchEnvironment,
    isRefetchCollection: state.variableSet.isRefetchCollection,
    isRefetchFolder: state.variableSet.isRefetchFolder
  }
}
export default connect(mapStateToProps)(ApiScreen);